import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  CreditCard, 
  Check, 
  X 
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionPage = ({ subscription, refreshSubscription }) => {
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [openingPortal, setOpeningPortal] = useState(false);
  const location = useLocation();

  // Check for returning from Stripe
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [location.search]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    
    if (attempts >= maxAttempts) {
      toast.error("Payment status check timed out. Please refresh the page.");
      return;
    }

    try {
      const response = await axios.get(`${API}/subscription/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success("Payment successful! Welcome to Par Seeker Pro!");
        await refreshSubscription();
        // Clear the URL params
        window.history.replaceState({}, '', '/subscription');
        return;
      } else if (response.data.status === 'expired') {
        toast.error("Payment session expired. Please try again.");
        window.history.replaceState({}, '', '/subscription');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error("Error checking payment:", error);
    }
  };

  const handleSubscribe = async (plan) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/subscription/checkout`, {
        origin_url: window.location.origin,
        plan: plan
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error("Failed to start checkout. Please try again.");
      console.error("Checkout error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setOpeningPortal(true);
    try {
      const response = await axios.post(`${API}/billing/portal`, {
        return_url: window.location.href
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error("Failed to open billing portal. Please try again.");
      console.error("Billing portal error:", error);
    } finally {
      setOpeningPortal(false);
    }
  };

  const isSubscribed = subscription?.is_subscribed;
  const trialDaysRemaining = subscription?.trial_days_remaining ?? 5;
  const trialExpired = subscription?.trial_expired ?? false;
  
  // Check if user still has free trial (5 days)
  const hasFreeTrial = !trialExpired;

  return (
    <div className="max-w-2xl mx-auto animate-fade-in" data-testid="subscription-page">

      {/* Free trial notice */}
      {!isSubscribed && (
        <div className={`${hasFreeTrial ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'} border rounded-xl p-4 mb-6`}>
          <div className="flex items-center justify-center gap-6">
            <div className="text-center">
              <p className={`text-2xl font-bold ${hasFreeTrial ? 'text-green-700' : 'text-amber-700'}`}>
                {hasFreeTrial ? trialDaysRemaining : 0}
              </p>
              <p className="text-sm text-stone-600">Days Left in Trial</p>
            </div>
          </div>
          {hasFreeTrial ? (
            <p className="text-center text-sm text-stone-600 mt-3">
              Enjoy your 10-day free trial! Full access to all features.
            </p>
          ) : (
            <p className="text-center text-sm text-amber-700 mt-3 font-medium">
              Your free trial has ended. Subscribe to continue tracking your handicap.
            </p>
          )}
        </div>
      )}

      {isSubscribed ? (
        <Card className="bg-[var(--ps-bg-surface)] border-[var(--ps-border)]">
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-[var(--ps-success)]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-[var(--ps-success)]" />
            </div>
            <h3 className="text-xl font-semibold text-[var(--ps-success)] mb-2">YOU'RE SUBSCRIBED!</h3>
            <p className="text-[var(--ps-text-secondary)] mb-2">
              Plan: <span className="font-medium text-[var(--ps-text-primary)]">{subscription.plan === '6month' ? '6 Months' : 'Monthly'}</span>
            </p>
            <p className="text-[var(--ps-text-secondary)] mb-4">
              Active until: {subscription.expires_at ? new Date(subscription.expires_at).toLocaleDateString() : 'N/A'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <NavLink to="/dashboard">
                <Button className="dark-btn-primary">GO TO QUALIFIER</Button>
              </NavLink>
              <Button 
                variant="outline" 
                onClick={handleManageBilling}
                disabled={openingPortal}
                data-testid="manage-billing-btn"
                className="border-[var(--ps-border)] text-[var(--ps-text-primary)] hover:bg-[var(--ps-bg-hover)]"
              >
                {openingPortal ? (
                  <div className="spinner w-4 h-4 border-[var(--ps-primary)] border-t-transparent mr-2" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Manage Billing
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Pricing Plans */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {/* Monthly Plan */}
            <Card 
              className={`cursor-pointer transition-all ${selectedPlan === 'monthly' ? 'ring-2 ring-green-600 border-green-600' : 'hover:border-stone-300'}`}
              onClick={() => setSelectedPlan('monthly')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-stone-900">Monthly</h3>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPlan === 'monthly' ? 'border-green-600 bg-green-600' : 'border-stone-300'}`}>
                    {selectedPlan === 'monthly' && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-3xl font-bold text-stone-900">$7.99</span>
                  <span className="text-stone-500">/month</span>
                </div>
                <p className="text-sm text-stone-500">Billed monthly. Cancel anytime.</p>
              </CardContent>
            </Card>

            {/* 6-Month Plan */}
            <Card 
              className={`cursor-pointer transition-all relative overflow-hidden ${selectedPlan === '6month' ? 'ring-2 ring-green-600 border-green-600' : 'hover:border-stone-300'}`}
              onClick={() => setSelectedPlan('6month')}
            >
              <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                BEST VALUE
              </div>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-stone-900">6 Months</h3>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPlan === '6month' ? 'border-green-600 bg-green-600' : 'border-stone-300'}`}>
                    {selectedPlan === '6month' && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-3xl font-bold text-stone-900">$39.99</span>
                  <span className="text-stone-500">/6 months</span>
                </div>
                <p className="text-sm text-stone-500">
                  <span className="line-through text-stone-400">$47.94</span> Save $7.95!
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Features */}
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">What's Included</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  'Unlimited score tracking',
                  'Automatic handicap calculation',
                  'Performance statistics & trends',
                  'Scorecard photo scanner (AI)',
                  'Unlimited golf courses',
                  'Score history & analytics',
                  'Golf Connect social features',
                  'Priority support'
                ].map((feature) => (
                  <li key={`feature-${feature.slice(0, 10)}`} className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-green-600" />
                    </div>
                    <span className="text-sm text-stone-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Subscribe Button */}
          <Button
            onClick={() => handleSubscribe(selectedPlan)}
            disabled={loading}
            className="btn-primary w-full h-12 mb-4"
            data-testid="subscribe-btn"
          >
            {loading ? (
              <div className="spinner w-5 h-5 border-white border-t-transparent" />
            ) : (
              <>
                <CreditCard className="w-5 h-5 mr-2" />
                Subscribe - {selectedPlan === '6month' ? '$39.99 for 6 months' : '$7.99/month'}
              </>
            )}
          </Button>

          {/* Continue free option */}
          {hasFreeTrial && (
            <div className="text-center">
              <NavLink to="/dashboard">
                <Button variant="ghost" className="text-green-700">
                  Continue with Free Trial →
                </Button>
              </NavLink>
            </div>
          )}

          <p className="text-xs text-stone-400 text-center mt-4">
            Secure payment powered by Stripe. Cancel anytime.
          </p>
        </>
      )}
    </div>
  );
};

export default SubscriptionPage;
